'use client';

import SearchSvgIcon from '@/shared/icons/SearchSvgIcon';
import {
  Drawer,
  IconButton,
  Icon,
  Portal,
  Stack,
  HStack,
  Center,
  InputGroup,
  Input,
  VStack,
  Text,
  Spinner
} from '@chakra-ui/react';
import React, { FC, useEffect, useRef, useState } from 'react'
import { I18nLink } from '../i18n/I18nLink';
import { Link as ChakraLink } from "@chakra-ui/react"
import Wrapper from './Wrapper';
import Image from 'next/image';
import { CloseButton } from '../ui/close-button';
import CategoryNavbar from '../shop/CategoryNavbar';
import { useDebounce } from 'react-use';
import { fetchProducts } from '@/services/products';
import { Product } from '@/types/product';
import ProductList from '../shop/ProductList';
import LanguageSwitcherMenu from '../custom/LanguageSwitcherMenu';
import { DrawerCloseTrigger } from '../ui/drawer';
import SearchSvgIcon2 from '@/shared/icons/SearchSvgIcon2';
import { HiColorSwatch } from 'react-icons/hi';
import { Button } from '../ui/button';
import { EmptyState } from '../ui/empty-state';
import { usePathname } from '@/i18n/navigation';
import { useTranslations } from "next-intl";
import { closeDrawer } from '@/shared/store/drawer';

interface Props {

}

const HeaderSearchBar: FC<Props> = () => {
  const t = useTranslations();
  const pathname = usePathname();

  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [data, setData] = useState<Product[]>([])

  const inputRef = useRef<HTMLInputElement | null>(null)

  const startElement = inputValue ? null : (
    <Icon boxSize={{ base: "5", md: "6" }}><SearchSvgIcon /></Icon>
  )

  const endElement = inputValue ? (
    <CloseButton
      size="xs"
      onClick={() => {
        setInputValue("")
        inputRef.current?.focus()
      }}
      me="-2"
    />
  ) : undefined

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useDebounce(() => {
    if (!inputValue.trim()) {
      setData([])
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

      ; (async () => {
        try {
          const results = await fetchProducts({ search: inputValue, limit: 10, filter: { hidden: false } })
          if (!cancelled) setData(results.data)
        } catch (e) {
          if (!cancelled) setData([])
        } finally {
          if (!cancelled) setLoading(false)
        }
      })()

    return () => {
      cancelled = true
    }
  }, 500, [inputValue])

  return (
    <Drawer.Root placement={"top"} size={"md"} open={open} onOpenChange={(e) => setOpen(e.open)}
      initialFocusEl={() => inputRef.current} preventScroll>
      <Drawer.Trigger asChild>
        <IconButton aria-label="Search products" variant={"plain"} size="xs">
          <Icon boxSize="6"><SearchSvgIcon2 /></Icon>
        </IconButton>
      </Drawer.Trigger>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content
            roundedBottom={"l3"}
          >
            <Drawer.Body p={0} pb={{ base: 4, sm: 10 }}>
              <Wrapper asChild>
                <Stack w={"100%"} gap={"40px"} py={"24px"}>
                  <HStack
                    gap={8}
                    w={"100%"}
                    py={"10px"}
                    position="sticky"
                    top={0}
                    zIndex={10}
                    bg="white"
                  >
                    <ChakraLink
                      as={I18nLink}
                      href="/"
                    >
                      <Center flexShrink={0}>
                        <Image
                          src="/logo.svg"
                          alt="QREP Logo"
                          width={40}
                          height={40}
                          style={{ width: "auto", height: "auto" }}
                          priority
                        />
                      </Center>
                    </ChakraLink>
                    <HStack as={"nav"} w={"100%"} justifyContent={"space-between"}>
                      <HStack alignSelf={"center"} gap={4} flexGrow={1} justifyContent={"flex-end"}>
                        {/* Кнопки серч бара*/}
                        <LanguageSwitcherMenu />
                        <DrawerCloseTrigger absolute={false} />
                      </HStack>
                    </HStack>
                  </HStack>
                  <Center>
                    <VStack gap={"40px"} w={"100%"}>
                      {/* Search input */}
                      <InputGroup startElement={startElement} endElement={endElement} w={{ base: "100%", md: "720px" }}>
                        <Input ref={inputRef} placeholder={t('search_placeholder')} variant={"flushed"}
                          size={{ base: "md", md: "lg" }}
                          value={inputValue}
                          onChange={(e) => {
                            setInputValue(e.currentTarget.value)
                          }} />
                      </InputGroup>
                      {!inputValue.trim() ? (
                        <>
                          {/* Категории */}
                          <CategoryNavbar isSearchMode />
                        </>
                      ) : (
                        loading ? (
                            <Spinner />
                        ) : data.length > 0 ? (
                          <Stack gap={8} w={"100%"}>
                            <Text textStyle={"bold16"}>{t('results')} ({data.length})</Text>

                            <ProductList
                              bordered={false}
                              products={data.slice(0, 8)}
                              gap={4}
                              cardProps={{ bg: "#ECECEC" }}
                            />
                          </Stack>
                        ) : (
                          <Stack gap={8} w={"100%"}>
                            <Text textStyle={"bold16"}>{t('products_sort')} ({data.length})</Text>
                            <EmptyState
                                    icon={<HiColorSwatch />}
                              title={t('no_products')}
                              description={t('try_change_filters_or_back')}
                            >
                              <I18nLink href="/" passHref>
                                <Button borderRadius={"none"}>{t('go_to_mainpage')}</Button>
                              </I18nLink>
                            </EmptyState>
                          </Stack>
                        )
                      )}
                    </VStack>
                  </Center>
                </Stack>
              </Wrapper>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}

export default HeaderSearchBar;